import { useEffect, useState } from "react";
import api, { apiErr, openFile } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MoveOutTotals } from "@/components/moveout/MoveOutTotals";
import { MoveOutCompare } from "@/components/moveout/MoveOutCompare";
import { ClipboardList, FileDown, Loader2, MessageSquareText, Hourglass } from "lucide-react";
import { toast } from "sonner";

export default function TenantMoveOut() {
  const [data, setData] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/tenant/move-out").then((r) => { setData(r.data); setComment(r.data.inspection?.tenant_comment || ""); });
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!comment.trim()) return toast.error("Write a comment first");
    setBusy(true);
    try { await api.post("/tenant/move-out/comment", { comment }); toast.success("Comment sent to your landlord"); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!data) return <Loading />;
  const insp = data.inspection;

  if (!insp) return <div><PageHeader title="Move-out" /><EmptyState icon={ClipboardList} title="No move-out inspection yet" subtitle="When your landlord completes the move-out walkthrough, the side-by-side comparison and deposit statement appear here." testid="tenant-moveout-none" /></div>;

  if (insp.status !== "submitted")
    return <div><PageHeader title="Move-out" /><EmptyState icon={Hourglass} title="Inspection in progress" subtitle="Your landlord has started the move-out inspection. You'll get an email with the statement as soon as it's finished." testid="tenant-moveout-draft" /></div>;

  return (
    <div>
      <PageHeader title="Move-out statement" subtitle={`${data.property?.name || "Your home"} · inspected ${new Date(insp.submitted_at).toLocaleDateString()} · compared against your move-in checklist`}
        action={insp.document_id && <Button variant="outline" className="gap-2" onClick={() => openFile(`/documents/${insp.document_id}/download`).catch((e) => toast.error(e.message))} data-testid="tenant-moveout-pdf"><FileDown className="h-4 w-4" /> Download PDF</Button>} />
      <div className="space-y-5" data-testid="tenant-moveout">
        <MoveOutTotals totals={insp.totals} locked />
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-5" data-testid="tenant-moveout-comment-card">
          <div className="flex items-center gap-2 text-sm font-bold text-indigo-900 mb-2"><MessageSquareText className="h-4 w-4" /> {insp.tenant_comment ? "Your comment" : "Disagree with something? Leave a comment"}</div>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} maxLength={2000} placeholder="Explain anything you'd like your landlord to reconsider…" className="bg-white" data-testid="tenant-moveout-comment" />
          <Button onClick={send} disabled={busy} className="mt-3 gap-2" data-testid="tenant-moveout-comment-send">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquareText className="h-4 w-4" />} {insp.tenant_comment ? "Update comment" : "Send comment"}</Button>
          {insp.tenant_comment_at && <p className="text-xs text-indigo-800/70 mt-2">Sent {new Date(insp.tenant_comment_at).toLocaleString()} · your landlord was notified.</p>}
        </div>
        <MoveOutCompare inspection={insp} />
      </div>
    </div>
  );
}
